import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowRight, Brain, Target, Users, Heart, Star, Sparkles, 
  MessageCircle, Zap, Crown, Play, SkipForward, TrendingUp
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';

const RefinedGameIntro = ({ player, onComplete, onSkip }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  return (
    <div 
      className="min-h-screen relative overflow-hidden bg-black"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/j2ey1004_16677AAA-CE81-41C3-B6C8-01247CBE5D2A.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-25">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(3px) brightness(1.4) saturate(1.6)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={500} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 md:p-8">
        <div className="max-w-6xl mx-auto text-center px-4">
          
          {/* Main Introduction Card */}
          <div className="bg-black/60 backdrop-blur-2xl rounded-3xl p-6 md:p-12 border border-cyan-400/20 shadow-2xl">
            
            {/* Epic Hook */}
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 md:mb-8"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 40px rgba(255, 255, 255, 0.8)'
              }}
            >
              You Are a Seeker Agent
            </h1>
            
            <h2 
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-cyan-300 mb-6 md:mb-8"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 25px rgba(34, 211, 238, 0.8)'
              }}
            >
              The Game IS Getting Things Done
            </h2>
            
            {/* Core Value Proposition - Mobile Optimized */}
            <div className="max-w-4xl mx-auto space-y-4 md:space-y-6 mb-8 md:mb-12">
              <p 
                className="text-base md:text-xl lg:text-2xl text-white/95 leading-relaxed px-2"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Sent from <strong className="text-yellow-300">10,000 generations in the future</strong> where universal collaboration already exists, 
                you're here to remember your mission: <strong className="text-cyan-300">transform everyday challenges into breakthrough adventures</strong> 
                while finding your true collaborators.
              </p>
              
              <p 
                className="text-sm md:text-lg lg:text-xl text-purple-200 leading-relaxed px-2"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                This isn't another productivity app or casual game. 
                <strong className="text-purple-300"> You're rewarded for completing your actual life projects</strong> - 
                discovered through strategic dialogue with M.ai.ry, your AI mission guide from the future.
              </p>
            </div>

            {/* Game Mechanics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              
              {/* Project Elucidation */}
              <div className="bg-purple-500/20 rounded-2xl p-6 border border-purple-400/30">
                <Target className="w-12 h-12 mx-auto mb-4 text-purple-300" />
                <h3 className="text-white text-lg font-bold mb-3">Dream Project Discovery</h3>
                <p className="text-purple-100 text-sm">
                  M.ai.ry asks strategic questions that reveal your deepest goals and transform them into actionable Project Salons.
                </p>
              </div>
              
              {/* AI Matchmaking */}
              <div className="bg-cyan-500/20 rounded-2xl p-6 border border-cyan-400/30">
                <Users className="w-12 h-12 mx-auto mb-4 text-cyan-300" />
                <h3 className="text-white text-lg font-bold mb-3">Seeker Agent Matching</h3>
                <p className="text-cyan-100 text-sm">
                  Find your perfect collaborators through trust-based algorithms that match complementary dreams and skills.
                </p>
              </div>
              
              {/* Trust Currency */}
              <div className="bg-green-500/20 rounded-2xl p-6 border border-green-400/30">
                <Heart className="w-12 h-12 mx-auto mb-4 text-green-300" />
                <h3 className="text-white text-lg font-bold mb-3">Trust as Currency</h3>
                <p className="text-green-100 text-sm">
                  Authentic vulnerability and successful collaboration become your reputation and advancement power.
                </p>
              </div>
              
              {/* Soul Score (Placeholder) */}
              <div className="bg-yellow-500/20 rounded-2xl p-6 border border-yellow-400/30">
                <Star className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
                <h3 className="text-white text-lg font-bold mb-3">Soul Score ✨</h3>
                <p className="text-yellow-100 text-sm">
                  Measures your authentic impact on collective perception and project actualization success.
                </p>
                <Badge className="bg-yellow-600/50 text-yellow-200 text-xs mt-2">Coming Soon</Badge>
              </div>
            </div>

            {/* Epic Mission Statement */}
            <div className="bg-gradient-to-r from-purple-900/50 to-cyan-900/50 rounded-2xl p-8 border border-white/20 mb-8">
              <h3 
                className="text-2xl font-bold text-white mb-4"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Your Mission from the Future Timeline
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <Brain className="w-10 h-10 mx-auto mb-3 text-purple-400" />
                  <h4 className="text-white font-bold mb-2">Project Activation</h4>
                  <p className="text-white/80 text-sm">
                    Transform overwhelming challenges into joyful collaborative adventures through strategic AI guidance.
                  </p>
                </div>
                
                <div className="text-center">
                  <Target className="w-10 h-10 mx-auto mb-3 text-cyan-400" />
                  <h4 className="text-white font-bold mb-2">Dream Elucidation</h4>
                  <p className="text-white/80 text-sm">
                    Discover your greatest plans through reverse prompts and perspective-swapping dialogue.
                  </p>
                </div>
                
                <div className="text-center">
                  <Users className="w-10 h-10 mx-auto mb-3 text-green-400" />
                  <h4 className="text-white font-bold mb-2">Collaborative Matching</h4>
                  <p className="text-white/80 text-sm">
                    Find your perfect project partners through perception field compatibility analysis.
                  </p>
                </div>
              </div>
            </div>

            {/* Sacred Outcome Promise */}
            <div className="bg-black/40 rounded-2xl p-6 border border-white/10 mb-8">
              <p 
                className="text-white/90 text-lg leading-relaxed italic"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                "Individual project completion serves collective awakening.<br/>
                Shared perspectives literally create new realities.<br/>
                <strong className="text-cyan-300">The game rewards you for following your bliss</strong> while finding your true collaborators."
              </p>
            </div>
            
            {/* Navigation */}
            <div className="flex items-center justify-center space-x-6">
              <Button
                onClick={onComplete}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-12 py-6 text-xl font-medium"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <Play className="w-6 h-6 mr-3" />
                Begin Your Mission
              </Button>
              
              <Button
                onClick={onSkip}
                variant="outline"
                className="text-white border-white/40 hover:bg-white/10 px-8 py-4"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                <SkipForward className="w-4 h-4 mr-2" />
                Skip to Game
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <UniversalHeartToggle />
      <PersistentFooter />
    </div>
  );
};

export default RefinedGameIntro;